import { useState, useRef, type FC, type ReactElement, type RefObject } from 'react';
import { graphql, useFragment } from 'react-relay';

import { ProductBaseTile } from './ProductBaseTile.tsx';
import { ProductImageCarousel } from './Carousel/ProductImageCarousel.tsx';

import { type MeasurementUnit } from 'dibs-intl/exports/units';
import {
    type ImageBackgroundColor,
    type OnClick,
    type SrcSet,
    type TileSize,
} from './constants.ts';

import { type BuyerProductCarousel_viewer$key } from './__generated__/BuyerProductCarousel_viewer.graphql.ts';
import { type BuyerProductCarousel_item$key } from './__generated__/BuyerProductCarousel_item.graphql.ts';
import { type BuyerProductCarousel_user$key } from './__generated__/BuyerProductCarousel_user.graphql.ts';
import { type BuyerProductCarousel_itemSearch$key } from './__generated__/BuyerProductCarousel_itemSearch.graphql.ts';

const viewerFragment = graphql`
    fragment BuyerProductCarousel_viewer on Viewer {
        ...ProductBaseTile_viewer
        ...ProductImageCarousel_viewer
    }
`;

const itemSearchFragment = graphql`
    fragment BuyerProductCarousel_itemSearch on ItemSearchQueryConnection {
        ...ProductBaseTile_itemSearch
    }
`;

const itemFragment = graphql`
    fragment BuyerProductCarousel_item on Item
    @argumentDefinitions(
        isTrade: { type: "Boolean", defaultValue: false }
        fetchVideo: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        priceBookName: { type: "String" }
        showSeller: { type: "Boolean!" }
    ) {
        ...ProductBaseTile_item
            @arguments(
                isTrade: $isTrade
                pageDisplayEnum: $pageDisplayEnum
                priceBookName: $priceBookName
                showSeller: $showSeller
            )
        ...ProductImageCarousel_item @arguments(fetchVideo: $fetchVideo)
    }
`;

const userFragment = graphql`
    fragment BuyerProductCarousel_user on User
    @argumentDefinitions(showSeller: { type: "Boolean!" }) {
        ...ProductBaseTile_user @arguments(showSeller: $showSeller)
    }
`;

type Props = {
    viewer: BuyerProductCarousel_viewer$key | null | undefined;
    itemSearch: BuyerProductCarousel_itemSearch$key | null | undefined;
    item: BuyerProductCarousel_item$key | null | undefined;
    user: BuyerProductCarousel_user$key | null | undefined;
    afterDisplay: boolean;
    beforeRender?: () => void;
    currency: string;
    disableLinks?: boolean;
    imageBackgroundColor?: ImageBackgroundColor;
    imageLoadVerticalOffset?: number;
    index: number;
    isSponsored?: boolean;
    itemsPerRow: number;
    hideRecentSoldPrice?: boolean;
    measurementUnit?: MeasurementUnit;
    shippingPrices?: Record<string, number> | null;
    onClick?: OnClick;
    onContentLoaded?: () => void;
    onContentVisible?: () => void;
    showMskuSwatch?: boolean;
    showBrowseSimilar?: boolean;
    showMeasurements?: boolean;
    showPrice?: boolean;
    showShippingCost?: boolean;
    showSwipeIndicator?: boolean;
    hasQuickView?: boolean;
    showQuickViewButton?: boolean;
    onQuickViewDetailsClick?: () => void;
    size: TileSize;
    favoritesComponent?: ReactElement | null;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    srcSet?: SrcSet;
    urgencySignal?: string | null;
    isShippingCostSbVariant?: boolean;
    tileElementRef?: RefObject<HTMLDivElement | null>;
};

export const BuyerProductCarousel: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    item: itemRef,
    user: userRef,
    afterDisplay,
    beforeRender,
    currency,
    disableLinks,
    imageBackgroundColor,
    imageLoadVerticalOffset,
    index,
    isSponsored,
    itemsPerRow,
    hideRecentSoldPrice,
    measurementUnit,
    shippingPrices,
    onClick,
    onContentLoaded,
    onContentVisible,
    onMouseEnter,
    onMouseLeave,
    size,
    showMskuSwatch = true,
    showBrowseSimilar,
    showMeasurements,
    showPrice,
    showShippingCost,
    hasQuickView = true,
    showQuickViewButton = true,
    srcSet,
    favoritesComponent,
    urgencySignal,
    isShippingCostSbVariant,
    showSwipeIndicator,
    tileElementRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const [isHovering, setIsHovering] = useState(false);
    const wasHovered = useRef(false);

    return (
        <ProductBaseTile
            viewer={viewer}
            itemSearch={itemSearch}
            item={item}
            user={user}
            afterDisplay={afterDisplay}
            beforeRender={beforeRender}
            currency={currency}
            index={index}
            isHovering={isHovering}
            isSponsored={isSponsored}
            hideRecentSoldPrice={hideRecentSoldPrice}
            measurementUnit={measurementUnit}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            shippingPrices={shippingPrices}
            onContentVisible={onContentVisible}
            setIsHovering={val => {
                setIsHovering(val);
                if (wasHovered.current) {
                    wasHovered.current = true;
                }
            }}
            size={size}
            showMskuSwatch={showMskuSwatch}
            showBrowseSimilar={showBrowseSimilar}
            showMeasurements={showMeasurements}
            showPrice={showPrice}
            hasQuickView={hasQuickView}
            showQuickViewButton={hasQuickView && showQuickViewButton}
            favoritesComponent={favoritesComponent}
            urgencySignal={urgencySignal}
            showShippingCost={showShippingCost}
            isShippingCostSbVariant={isShippingCostSbVariant}
            tileElementRef={tileElementRef}
        >
            <ProductImageCarousel
                viewer={viewer}
                item={item}
                imageBackgroundColor={imageBackgroundColor}
                disableLinks={disableLinks}
                imageLoadVerticalOffset={imageLoadVerticalOffset}
                index={index}
                itemsPerRow={itemsPerRow}
                isHovering={isHovering}
                onClick={onClick}
                onContentLoaded={onContentLoaded}
                size={size}
                srcSet={srcSet}
                wasHovered={wasHovered.current}
                showSwipeIndicator={showSwipeIndicator}
            />
        </ProductBaseTile>
    );
};
